import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import caseStudyMarcus from '@/assets/case-study-marcus.jpg';
import caseStudySarah from '@/assets/case-study-sarah.jpg';
import caseStudyDavid from '@/assets/case-study-david.jpg';
import caseStudyJennifer from '@/assets/case-study-jennifer.jpg';
interface CaseStudy {
  id: string;
  clientName: string;
  photo: string;
  yearsInSales: string;
  category: string;
  initialState: string;
  transformationAchieved: string;
  timeframe: string;
  closingInsight: string;
}
const caseStudies: CaseStudy[] = [{
  id: "salesperson-transition-1",
  clientName: "Marcus T.",
  photo: caseStudyMarcus,
  yearsInSales: "6 years",
  category: "Sales to F&I",
  initialState: "Income fluctuating between $4,200 and $11,800 monthly. No understanding of how deals funded. Felt overlooked for F&I opportunities despite tenure. Had been asking about F&I for 18 months with no clear path forward.",
  transformationAchieved: "Completed BoxClose certification in 5 months. Transitioned from Senior Sales Consultant to F&I Manager at a new franchise. First three months in F&I: income ranged between $12,800 and $14,200. Received 3 offers from 4 interviews.",
  timeframe: "5 months",
  closingInsight: "This transition happened because the client stopped trying to get into F&I and started preparing for F&I. The difference was learning how deals actually fund, how compliance actually protects careers, and how structure creates consistency."
}, {
  id: "salesperson-transition-2",
  clientName: "Sarah M.",
  photo: caseStudySarah,
  yearsInSales: "12 years",
  category: "Sales to F&I",
  initialState: "4 \"salesperson of the year\" awards but severe burnout from floor traffic uncertainty. Income swings between $3,800 and $16,200 monthly. Health issues emerging from chronic work stress. Considering leaving the industry entirely.",
  transformationAchieved: "Transitioned to F&I Manager role at a smaller, lower-volume dealership. Monthly income stabilized between $11,400 and $13,800. Eliminated floor traffic dependency. Reported significant improvement in stress-related health markers within 4 months of transition.",
  timeframe: "6 months",
  closingInsight: "This case demonstrates that F&I isn't just about income—it's about career sustainability. The skills that made someone excellent at sales don't automatically transfer to F&I, but they can be channeled correctly with proper training."
}, {
  id: "salesperson-transition-3",
  clientName: "David K.",
  photo: caseStudyDavid,
  yearsInSales: "4 years",
  category: "Sales to F&I",
  initialState: "Top-performing sales consultant at a domestic franchise. Consistently moved 18-22 units monthly but income remained unpredictable due to backend splits and desk decisions. Watched two junior colleagues get promoted to F&I ahead of him.",
  transformationAchieved: "Earned BoxClose certification and presented documented competency to ownership. Promoted internally to F&I within 4 months. First full quarter averaged $14,600/month with consistent deal flow. No longer dependent on lot traffic or inventory allocation.",
  timeframe: "4 months",
  closingInsight: "The promotion happened because he demonstrated understanding of funding requirements and compliance frameworks—not because he asked for it. Preparation created the opportunity."
}, {
  id: "salesperson-transition-4",
  clientName: "Jennifer R.",
  photo: caseStudyJennifer,
  yearsInSales: "8 years",
  category: "Sales to F&I",
  initialState: "Bounced between three dealerships as a sales consultant. Best months hit $14,000 but averaged closer to $7,200. No formal path to F&I despite multiple requests. Previous managers said she \"wasn't ready\" without explaining what ready meant.",
  transformationAchieved: "Completed BoxClose certification and interviewed at a competing dealership with her portfolio. Hired as F&I Manager within 3 months of starting the program. Income stabilized between $13,200 and $15,800 monthly in first quarter.",
  timeframe: "3 months",
  closingInsight: "She stopped waiting for someone to give her the opportunity and created her own leverage. The certification and documented knowledge made her undeniable in interviews."
}];
const CaseStudies = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  return <section id="case-studies" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-primary tracking-wider uppercase mb-3">
            Case Studies
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0e172a]">
            Documented Transformations
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Real professionals. Verified outcomes. No exaggeration.
          </p>
        </div>

        <div className="space-y-8">
          {caseStudies.map(study => <div key={study.id} className="bg-slate-900 border border-slate-700 overflow-hidden rounded-lg">
              {/* Table Layout */}
              <table className="w-full border-collapse">
                <tbody>
                  {/* Client Name with Photo Row */}
                  <tr className="border-b border-slate-700">
                    <td className="px-6 py-4 bg-slate-800 font-medium text-white w-1/4 border-r border-slate-700">
                      Client
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-4">
                        <span className="text-white font-medium text-lg">{study.clientName}</span>
                        <img src={study.photo} alt={study.clientName} className="w-20 h-20 md:w-28 md:h-28 rounded-full object-cover border-2 border-slate-600" />
                      </div>
                    </td>
                  </tr>

                  {/* Years in Sales Row */}
                  <tr className="border-b border-slate-700">
                    <td className="px-6 py-4 bg-slate-800 font-medium text-white border-r border-slate-700">
                      Years in Sales
                    </td>
                    <td className="px-6 py-4 text-white text-center font-medium">
                      {study.yearsInSales}
                    </td>
                  </tr>

                  {/* Initial State Row */}
                  <tr className="border-b border-slate-700">
                    <td className="px-6 py-4 bg-slate-800 font-medium text-white border-r border-slate-700 align-top">
                      Initial State
                    </td>
                    <td className="px-6 py-4 text-white/70">
                      {study.initialState}
                    </td>
                  </tr>

                  {/* Transformation Achieved Row */}
                  <tr className="border-b border-slate-700">
                    <td className="px-6 py-4 bg-slate-800 font-medium text-white border-r border-slate-700 align-top">
                      Transformation Achieved
                    </td>
                    <td className="px-6 py-4 text-white/70">
                      {study.transformationAchieved}
                    </td>
                  </tr>

                  {/* Timeframe Row */}
                  <tr className={expandedId === study.id ? "border-b border-slate-700" : ""}>
                    <td className="px-6 py-4 bg-slate-800 font-medium text-white border-r border-slate-700">
                      Timeframe
                    </td>
                    <td className="px-6 py-4 text-white text-center font-medium">
                      {study.timeframe}
                    </td>
                  </tr>

                  {/* Expanded: Closing Insight Row */}
                  {expandedId === study.id && <tr>
                      <td className="px-6 py-4 bg-slate-800 font-medium text-white border-r border-slate-700 align-top">
                        Closing Insight
                      </td>
                      <td className="px-6 py-4 text-white/70 italic">
                        "{study.closingInsight}"
                      </td>
                    </tr>}
                </tbody>
              </table>

              {/* Expand/Collapse Button */}
              <button onClick={() => toggleExpand(study.id)} className="w-full px-6 py-3 bg-slate-800 border-t border-slate-700 flex items-center justify-center gap-2 text-sm text-white/60 hover:text-white hover:bg-slate-700 transition-colors">
                {expandedId === study.id ? <>
                    <span>Show Less</span>
                    <ChevronUp className="w-4 h-4" />
                  </> : <>
                    <span>View Closing Insight</span>
                    <ChevronDown className="w-4 h-4" />
                  </>}
              </button>
            </div>)}
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground">
            All case studies represent documented outcomes from BoxClose participants.
            <br />
            Results vary based on individual effort, market conditions, and dealership environment.
          </p>
        </div>
      </div>
    </section>;
};
export default CaseStudies;